import React, { useCallback, useState } from "react";
import { TfiReload, TfiSave } from "react-icons/tfi";
import { simpleAlert } from "react-simple-dialogs";
import { CircleLoader } from "react-spinners";
import { Allotment } from "allotment";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";

import PricingsList from "@pages/ProductCatalog/PricingsList.tsx";
import {
  createProduct,
  type PricingInput,
  updateProduct,
} from "@pages/ProductCatalog/productApi.ts";
import {
  editEntitlementsState,
  editProductPricingsState,
  NEW_PRODUCT_ID,
  newProductNameState,
  productDetails,
  productSummaries,
  resetEdits,
  selectedProductState,
} from "@pages/ProductCatalog/store.ts";

import { Button } from "./components/Button.tsx";
import { FullPageDiv } from "./components/FullPageDiv.tsx";
import { Toolbar } from "./components/Toolbar.tsx";
import EntitlementsList from "./EntitlementsList.tsx";
import ProductsList from "./ProductsList.tsx";

import "allotment/dist/style.css";

const Fallback = (
  <FullPageDiv overlay={true} justify={"center"}>
    <CircleLoader />
  </FullPageDiv>
);

const ProductEditor = () => {
  const [isSaving, setIsSaving] = useState(false);

  const [productId, setProductId] = useRecoilState(selectedProductState);
  const [, setProduct] = useRecoilState(productDetails(productId));
  const setProducts = useSetRecoilState(productSummaries);
  const setResetEditsTrigger = useSetRecoilState(resetEdits);
  const [newProductName, setNewProductName] =
    useRecoilState(newProductNameState);

  const editedPricings = useRecoilValue(editProductPricingsState);
  const selectedEntitlements = useRecoilValue(editEntitlementsState);

  const saveProduct = useCallback(async () => {
    if (productId === undefined) {
      return;
    }
    setIsSaving(true);
    try {
      const isNew = productId === NEW_PRODUCT_ID;

      // Update is replace-all: send every non-deleted, non-blank row (the grid
      // keeps a trailing empty row with no id). `active`/`promotionSlug` are
      // sent explicitly so a round-trip doesn't resurrect or wipe them.
      const pricings: PricingInput[] = editedPricings
        .filter(({ id, deleted }) => id && !deleted)
        .map(
          ({
            amount,
            strikeThroughAmount,
            currency,
            countryCode,
            startDate,
            endDate,
            promotionSlug,
            active,
          }) => ({
            amount,
            strikeThroughAmount,
            currency,
            countryCode,
            startDate: startDate ?? null,
            endDate: endDate ?? null,
            promotionSlug: promotionSlug ?? null,
            active,
          }),
        );

      const updatedProduct = isNew
        ? await createProduct({
            name: newProductName,
            entitlements: selectedEntitlements,
            pricings,
          })
        : await updateProduct(productId, {
            entitlements: selectedEntitlements,
            pricings,
          });

      setProducts((products) => {
        const index = products.findIndex(({ id }) => id === productId);
        if (index === -1) {
          return [...products, updatedProduct];
        }
        return [
          ...products.slice(0, index),
          updatedProduct,
          ...products.slice(index + 1),
        ];
      });

      if (isNew) {
        setNewProductName("");
        // Switching to the real id makes productDetails load the saved product.
        setProductId(updatedProduct.id);
      } else {
        setProduct(updatedProduct);
      }
    } catch (err) {
      // adminApi throws with the BE's message (duplicate pricing, existing SKU,
      // entitlement in use, ...). Surface it — retrying is the wrong advice for
      // all of them — and keep the edited grid state so the admin can fix it.
      await simpleAlert({
        title: "Save failed",
        message: err instanceof Error ? err.message : "Could not save product.",
        closeLabel: "Close",
      });
    } finally {
      setIsSaving(false);
    }
  }, [productId, newProductName, selectedEntitlements, editedPricings]);

  // NEW_PRODUCT_ID is 0 (falsy), so guard on `undefined` — otherwise the editor
  // never renders for a brand-new product and there's no way to reach Save.
  if (productId === undefined) {
    return (
      <FullPageDiv justify={"center"}>Select a product to edit.</FullPageDiv>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {isSaving && Fallback}
      <Toolbar>
        <Button onClick={() => setResetEditsTrigger(Math.random())}>
          <TfiReload />
        </Button>
        <Button onClick={saveProduct}>
          <TfiSave />
        </Button>
      </Toolbar>
      <Allotment defaultSizes={[20, 80]}>
        <EntitlementsList />
        <PricingsList />
      </Allotment>
    </div>
  );
};

export default function ProductCatalog() {
  return (
    <FullPageDiv>
      <Allotment defaultSizes={[22, 78]}>
        <ProductsList />
        <React.Suspense fallback={Fallback}>
          <ProductEditor />
        </React.Suspense>
      </Allotment>
    </FullPageDiv>
  );
}
